import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/ui/radio-group";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, User, Car, Store } from "lucide-react";

const emptyCustomer = { name: "", phone: "", email: "", address: "", notes: "", type: "customer" };

const TYPE_META = {
  customer: {
    label: "Customer",
    cls: "bg-[#EAF0F4] text-[#3D5A6C] border border-[#B8CEDA]",
    icon: User,
  },
  dealer: {
    label: "Dealer",
    cls: "bg-[#F4D35E]/30 text-[#8B6914] border border-[#E5A82E]/40",
    icon: Store,
  },
};

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyCustomer);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    const [c, v] = await Promise.all([api.get("/customers"), api.get("/vehicles")]);
    setCustomers(c.data);
    setVehicles(v.data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyCustomer);
    setDialogOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name,
      phone: c.phone || "",
      email: c.email || "",
      address: c.address || "",
      notes: c.notes || "",
      type: c.type || "customer",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    try {
      if (editing) {
        await api.put(`/customers/${editing.id}`, form);
        toast.success("Customer updated");
      } else {
        await api.post("/customers", form);
        toast.success("Customer added");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error("Failed to save customer");
    }
  };

  const del = async () => {
    try {
      await api.delete(`/customers/${confirmDelete.id}`);
      toast.success("Customer deleted");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error("Failed to delete customer");
    }
  };

  const filtered = customers.filter((c) => {
    // Type filter
    if (typeFilter !== "all") {
      const t = c.type || "customer";
      if (t !== typeFilter) return false;
    }
    if (!query) return true;
    const q = query.toLowerCase();
    const plates = vehicles
      .filter((v) => v.customer_id === c.id)
      .map((v) => (v.license_plate || "").toLowerCase())
      .join(" ");
    return (
      c.name.toLowerCase().includes(q) ||
      (c.phone || "").toLowerCase().includes(q) ||
      (c.email || "").toLowerCase().includes(q) ||
      plates.includes(q)
    );
  });

  const customerCount = customers.filter((c) => (c.type || "customer") === "customer").length;
  const dealerCount = customers.filter((c) => c.type === "dealer").length;

  return (
    <div>
      <PageHeader
        testid="customers-header"
        title="Vehicle Register"
        subtitle="Every vehicle in the shop — labelled by registration number."
        actions={
          <>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-muted" />
              <Input
                data-testid="customer-search-input"
                placeholder="Search plate, name, phone..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-9 w-72 rounded-lg border-2 border-warm bg-white"
              />
            </div>
            <Button
              data-testid="add-customer-btn"
              onClick={openCreate}
              className="bg-[#1A1A1A] hover:bg-[#2D2D2D] text-white rounded-xl font-medium"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Customer
            </Button>
          </>
        }
      />

      {/* Type filter tabs */}
      <Tabs value={typeFilter} onValueChange={setTypeFilter} className="mb-6">
        <TabsList className="bg-[#F3F2EE] p-1 rounded-xl" data-testid="client-type-tabs">
          <TabsTrigger value="all" data-testid="filter-type-all" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-card">
            All ({customers.length})
          </TabsTrigger>
          <TabsTrigger value="customer" data-testid="filter-type-customer" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-card">
            <User className="w-3.5 h-3.5 mr-1.5" />
            Customers ({customerCount})
          </TabsTrigger>
          <TabsTrigger value="dealer" data-testid="filter-type-dealer" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-card">
            <Store className="w-3.5 h-3.5 mr-1.5" />
            Dealers ({dealerCount})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <Card className="p-10 text-center text-slate-muted rounded-2xl border-warm">Loading customers…</Card>
      ) : filtered.length === 0 && customers.length === 0 ? (
        <EmptyState
          testid="customers-empty"
          image="https://images.unsplash.com/photo-1603039548092-fff996c2441c?crop=entropy&cs=srgb&fm=jpg&w=400&q=70"
          title="No customers yet"
          description="Add your first customer to start creating work orders."
          action={
            <Button
              onClick={openCreate}
              className="bg-[#1A1A1A] hover:bg-[#2D2D2D] text-white rounded-xl"
              data-testid="empty-add-customer"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Customer
            </Button>
          }
        />
      ) : (
        <Card className="rounded-2xl border-warm shadow-card overflow-hidden" data-testid="customers-table">
          <Table>
            <TableHeader className="bg-[#F3F2EE]">
              <TableRow>
                <TableHead className="text-[#3D405B] font-semibold uppercase tracking-wider text-xs">Vehicle Reg #</TableHead>
                <TableHead className="text-[#3D405B] font-semibold uppercase tracking-wider text-xs">Owner</TableHead>
                <TableHead className="text-[#3D405B] font-semibold uppercase tracking-wider text-xs">Type</TableHead>
                <TableHead className="text-[#3D405B] font-semibold uppercase tracking-wider text-xs">Phone</TableHead>
                <TableHead className="text-[#3D405B] font-semibold uppercase tracking-wider text-xs">Email</TableHead>
                <TableHead className="text-right"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((c) => {
                const plates = vehicles
                  .filter((v) => v.customer_id === c.id)
                  .map((v) => v.license_plate)
                  .filter(Boolean);
                const ct = c.type || "customer";
                const tmeta = TYPE_META[ct] || TYPE_META.customer;
                const TIcon = tmeta.icon;
                return (
                <TableRow key={c.id} data-testid={`customer-row-${c.id}`} className="hover:bg-[#FAF9F6]">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-[#F3F2EE] flex items-center justify-center text-[#E5A82E]">
                        <Car className="w-4 h-4" />
                      </div>
                      {plates.length === 0 ? (
                        <span className="text-sm text-slate-muted italic">No vehicle registered</span>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {plates.map((p) => (
                            <span
                              key={p}
                              className="font-mono text-sm font-semibold text-[#1A1A1A] bg-[#F4D35E]/30 border border-[#E5A82E]/40 rounded-md px-2 py-0.5"
                            >
                              {p.toUpperCase()}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 text-[#2B2D42]">
                      <TIcon className="w-3.5 h-3.5 text-slate-muted" />
                      {c.name}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider ${tmeta.cls}`}
                      data-testid={`customer-type-${c.id}`}
                    >
                      <TIcon className="w-3 h-3" />
                      {tmeta.label}
                    </span>
                  </TableCell>
                  <TableCell className="text-[#2B2D42]">{c.phone || "—"}</TableCell>
                  <TableCell className="text-[#2B2D42]">{c.email || "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`edit-customer-${c.id}`}
                        onClick={() => openEdit(c)}
                        className="rounded-lg text-[#3D405B] hover:bg-[#F3F2EE]"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`delete-customer-${c.id}`}
                        onClick={() => setConfirmDelete(c)}
                        className="rounded-lg text-[#E07A5F] hover:bg-[#FAE8E4]"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                );
              })}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-10 text-slate-muted">
                    No matches for &quot;{query}&quot;
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Add/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-2xl max-w-lg" data-testid="customer-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {editing ? "Edit Customer" : "Add Customer"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-semibold text-[#3D405B]">Client Type</Label>
              <RadioGroup
                value={form.type || "customer"}
                onValueChange={(val) => setForm({ ...form, type: val })}
                className="grid grid-cols-2 gap-3 mt-1.5"
                data-testid="customer-type-radio"
              >
                <label
                  className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                    (form.type || "customer") === "customer"
                      ? "border-[#1A1A1A] bg-[#FAF9F6]"
                      : "border-warm bg-white hover:bg-[#FAF9F6]"
                  }`}
                >
                  <RadioGroupItem value="customer" data-testid="type-radio-customer" />
                  <User className="w-4 h-4 text-slate-muted" />
                  <span className="text-sm font-medium text-[#2B2D42]">Customer</span>
                </label>
                <label
                  className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                    form.type === "dealer"
                      ? "border-[#E5A82E] bg-[#F4D35E]/10"
                      : "border-warm bg-white hover:bg-[#FAF9F6]"
                  }`}
                >
                  <RadioGroupItem value="dealer" data-testid="type-radio-dealer" />
                  <Store className="w-4 h-4 text-[#8B6914]" />
                  <span className="text-sm font-medium text-[#2B2D42]">Dealer</span>
                </label>
              </RadioGroup>
            </div>
            <div>
              <Label className="text-sm font-semibold text-[#3D405B]">Name *</Label>
              <Input
                data-testid="customer-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Jane Doe"
                className="mt-1.5 rounded-lg border-2 border-warm"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-semibold text-[#3D405B]">Phone</Label>
                <Input
                  data-testid="customer-phone-input"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="(555) 555-1234"
                  className="mt-1.5 rounded-lg border-2 border-warm"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold text-[#3D405B]">Email</Label>
                <Input
                  data-testid="customer-email-input"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="jane@example.com"
                  className="mt-1.5 rounded-lg border-2 border-warm"
                />
              </div>
            </div>
            <div>
              <Label className="text-sm font-semibold text-[#3D405B]">Address</Label>
              <Input
                data-testid="customer-address-input"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="123 Main St"
                className="mt-1.5 rounded-lg border-2 border-warm"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold text-[#3D405B]">Notes</Label>
              <Textarea
                data-testid="customer-notes-input"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
                className="mt-1.5 rounded-lg border-2 border-warm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="rounded-xl border-2 border-warm"
              data-testid="cancel-customer-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={save}
              className="bg-[#1A1A1A] hover:bg-[#2D2D2D] text-white rounded-xl"
              data-testid="save-customer-btn"
            >
              {editing ? "Save Changes" : "Add Customer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete customer?</AlertDialogTitle>
            <AlertDialogDescription>
              This will also delete every vehicle registered to {confirmDelete?.name} and every repair order linked to them. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={del}
              className="bg-[#E07A5F] hover:bg-[#C86A4F] text-white rounded-xl"
              data-testid="confirm-delete-customer"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
